import {
  cacheKeys,
  FeatureName,
  Group,
  useAllWorkspaceGroups,
  useCreateGroup,
  useCurrentWorkspace,
  useCustomMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import {
  ColumnData,
  SelectionStatus,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { Asset } from './asset';
import { AssetCard } from './assetCard';
import { AssetComponent, SelectedAssetRow } from './assetComponent';
import { AssetFilters } from './assetFilters';
import { Assets as AssetsOld } from './assets-old';
import { AssetsFilters } from './assetsFilters';
import { AssetsGroupsPicker } from './assetsGroupsPicker';
import {
  AssetsSelectionPanelHeaderActions,
  AssetsSelectionPanelMain,
} from './assetsSelectionPanel';
import { EditAssetComponent } from './edit';
import { EditAsset as EditAssetOld } from './edit-old';
import EditGroup from './editGroup';

export const Assets = () => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  const { '*': rawSplat } = useParams();
  const splat = rawSplat?.replace(/\/$/, '');

  if (isCropCircleEnabled) {
    return <AssetsBase />;
  }

  // Legacy fallbacks: the wildcard route catches all /assets/* paths, but
  // the old components need distinct routes for detail and edit views.
  if (splat?.endsWith('/edit')) {
    return <EditAssetOld />;
  }

  if (splat && !splat.includes('/')) {
    return <Asset />;
  }

  return <AssetsOld />;
};

const ADD_ASSETS_TO_GROUPS = `
  mutation Asset($input: AddAssetsToGroupsInput!) {
    asset {
      addAssetsToGroups(input: $input) {
        success
        message
      }
    }
  }
`;

export const AssetsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const { '*': splat } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { createGroup } = useCreateGroup();
  const workspace = useCurrentWorkspace(true);
  const editPathMatch = location.pathname.match(
    /^\/assets\/([^/]+)\/edit\/?$/
  );
  const isEditing = !!editPathMatch;
  const assetId = editPathMatch?.[1] ?? splat ?? '';
  const currentWorkspaceId = workspace?.id;

  const { groups: availableGroups } = useAllWorkspaceGroups(currentWorkspaceId);

  // --- Edit-panel groups state (existing behavior) ---
  const [editSelectedGroups, setEditSelectedGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [fourthPanelMode, setFourthPanelMode] = useState<
    'groups' | undefined
  >(undefined);
  useEffect(() => {
    if (!isEditing) setFourthPanelMode(undefined);
  }, [isEditing]);
  const groupsResetRef = useRef<(() => void) | undefined>(undefined);

  // --- Checkbox selection state ---
  const [rowsSelected, setRowsSelected] = useState<SelectedAssetRow[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [isGroupsPickerOpen, setIsGroupsPickerOpen] = useState(false);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );

  const hasSelection = rowsSelected.length > 0;

  const { mutateAsync: addAssetsToGroups } = useCustomMutation({
    query: ADD_ASSETS_TO_GROUPS,
  });

  // Seed groups panel from selected assets' groups:
  //   - 1 asset selected  → its groups are pre-selected.
  //   - 2+ with exact same groups → pre-selected.
  //   - 2+ with any difference   → empty (user picks fresh).
  //   - 0 selected → clear and close picker.
  //
  // Match by name and re-hydrate real IDs from `availableGroups`.
  useEffect(() => {
    if (!hasSelection) {
      setSelectedGroups([]);
      setIsGroupsPickerOpen(false);
      return;
    }

    const [first, ...rest] = rowsSelected;
    const firstNames = (first.groups ?? [])
      .map((g) => g.name)
      .filter((name): name is string => !!name)
      .sort();

    const allSame = rest.every((row) => {
      const rowNames = (row.groups ?? [])
        .map((g) => g.name)
        .filter((name): name is string => !!name)
        .sort();
      return (
        rowNames.length === firstNames.length &&
        rowNames.every((n, i) => n === firstNames[i])
      );
    });

    if (!allSame) {
      setSelectedGroups([]);
      return;
    }

    const nextSelected = firstNames
      .map((name) => availableGroups.find((g) => g.name === name))
      .filter((g): g is Group => !!g)
      .map((g) => ({ id: g.id, name: g.name }));

    setSelectedGroups(nextSelected);
  }, [rowsSelected, availableGroups, hasSelection]);

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const handleGroupsChange = useCallback(
    async (nextGroups: Group[]) => {
      const previousIds = new Set(selectedGroups.map((g) => g.id));
      const added = nextGroups.filter((g) => !previousIds.has(g.id));

      setSelectedGroups(nextGroups);

      if (added.length === 0 || !currentWorkspaceId) return;

      await addAssetsToGroups({
        input: {
          groups: added.map((g) => ({ id: g.id })),
          assetIds: rowsSelectedIds,
          workspaceId: currentWorkspaceId,
        },
      });
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('asset').all,
      });
    },
    [
      selectedGroups,
      rowsSelectedIds,
      currentWorkspaceId,
      addAssetsToGroups,
      queryClient,
    ]
  );

  // --- Column definitions ---

  const leftColumn = {
    title: 'assets.menu.title',
    mainComponent: (
      <AssetComponent
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
    ),
    headerComponent: <AssetsFilters />,
    disableScroll: true,
  };

  // rightColumn: selection panel takes priority over every other mode.
  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection || !currentWorkspaceId) return undefined;

    const title =
      rowsSelected.length === 1
        ? t('assets.topBar.assetSelected')
        : t('assets.topBar.assetsSelected', {
            count: rowsSelected.length,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: (
        <AssetsSelectionPanelMain
          selectedGroups={selectedGroups}
          onGroupsChange={handleGroupsChange}
          onCreateGroup={createGroup}
          onOpenPicker={() => setIsGroupsPickerOpen(true)}
        />
      ),
      headerComponent: (
        <AssetsSelectionPanelHeaderActions onReset={clearSelection} />
      ),
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [
    hasSelection,
    currentWorkspaceId,
    rowsSelected,
    selectedGroups,
    handleGroupsChange,
    clearSelection,
    createGroup,
    t,
    i18n,
  ]);

  const detailRightColumn: ColumnData | undefined = assetId
    ? {
        title: 'assets.menu.title',
        mainComponent: <AssetCard assetId={assetId} />,
        headerComponent: <AssetFilters assetId={assetId} />,
        showBackButton: true,
        backButtonTransition: SlideAction.SINGLE,
        onBackButtonPress: () => navigate('/assets'),
      }
    : undefined;

  // Priority: selection > caller override (filters page) > asset detail.
  const rightColumn =
    selectionRightColumn ?? rightColumnOverride ?? detailRightColumn;

  // thirdColumn: edit form (when editing) OR groups picker (from selection).
  const thirdColumn = useMemo(() => {
    if (isEditing) {
      return {
        title: 'asset.editTitle',
        showBackButton: true,
        onBackButtonPress: () => navigate(`/assets/${assetId}`),
        disableScroll: true,
        mainComponent: (
          <EditAssetComponent
            assetId={assetId}
            selectedGroups={editSelectedGroups}
            onGroupsChange={setEditSelectedGroups}
            onOpenGroupEditor={() => setFourthPanelMode('groups')}
          />
        ),
      };
    }

    if (isGroupsPickerOpen && hasSelection && currentWorkspaceId) {
      return {
        title: 'asset.groups',
        mainComponent: (
          <AssetsGroupsPicker
            selectedGroups={selectedGroups}
            onGroupsChange={handleGroupsChange}
            onCreateGroup={createGroup}
            workspaceId={currentWorkspaceId}
          />
        ),
        showBackButton: true,
        onBackButtonPress: () => setIsGroupsPickerOpen(false),
        disableScroll: true,
      };
    }

    return undefined;
  }, [
    isEditing,
    isGroupsPickerOpen,
    hasSelection,
    currentWorkspaceId,
    assetId,
    editSelectedGroups,
    selectedGroups,
    handleGroupsChange,
    createGroup,
    navigate,
  ]);

  // fourthColumn: group editor from within the edit form (existing behavior).
  const fourthColumn =
    isEditing && fourthPanelMode === 'groups'
      ? {
          title: 'asset.groups',
          showBackButton: true,
          onBackButtonPress: () => setFourthPanelMode(undefined),
          disableScroll: true,
          headerComponent: (
            <View>
              <HeaderResetButton onReset={() => groupsResetRef.current?.()} />
            </View>
          ),
          mainComponent: (
            <EditGroup
              assetId={assetId}
              selectedGroups={editSelectedGroups}
              onGroupsChange={setEditSelectedGroups}
              resetRef={groupsResetRef}
            />
          ),
        }
      : undefined;

  const viewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride || assetId
      ? ViewMode.DUAL
      : ViewMode.SINGLE);

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      thirdColumn={thirdColumn}
      fourthColumn={fourthColumn}
      viewMode={viewMode}
    />
  );
};
